/* eslint-disable react/jsx-no-target-blank */
import { auto } from '@popperjs/core';

import Tippy from '@tippyjs/react';
import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { Image } from 'react-bootstrap';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Badge } from 'app/components/Badge';
import Typography from 'app/components/Typography';

import { getTotalUnreadAlert } from 'app/modules/alerts/core/request';
import { useAlertStore } from 'app/modules/alerts/store';
import { useAuth } from 'app/modules/auth';
import { getHeartBeat, getTotalUnreadNoti } from 'app/modules/notifications/core/request';
import { useNotificationStore } from 'app/modules/notifications/store';
import { getOrganizations } from 'app/modules/organizations/core/requests';
import { getOfflineCount, useHeartBeatStore } from 'app/modules/system-settings/locations/store';

import { useWindowSize } from 'app/helpers/screen';
import { generateRandomColor, getInitialsCharacter } from 'app/helpers/utils';
import { useBoolean } from 'app/hooks';
import { usePolicyInUser } from 'app/hooks/policy';
import { SocketClient } from 'app/hooks/socket';
import { isNull } from 'lodash';

import { PATH, PUBLIC_PATHS } from 'app/constants/path';

import { ReactComponent as DeviceIcon } from 'app/assets/icons/device-icon.svg';
import { ReactComponent as ReportIcon } from 'app/assets/icons/diagram.svg';
import { ReactComponent as DashboardIcon } from 'app/assets/icons/element-3.svg';
import { ReactComponent as CompanyInfoIcon } from 'app/assets/icons/hospital.svg';
import { ReactComponent as ArrowUpDownIcon } from 'app/assets/icons/icon-arrow-up-down.svg';
import { ReactComponent as NotificationIcon } from 'app/assets/icons/notification.svg';
import { ReactComponent as ItemIcon } from 'app/assets/icons/pill-icon.svg';
import { ReactComponent as UserIcon } from 'app/assets/icons/profile-2user.svg';
import { ReactComponent as RackIcon } from 'app/assets/icons/rack-icon.svg';
import { ReactComponent as StockRunIcon } from 'app/assets/icons/stock-icon.svg';
import { ReactComponent as WardIcon } from 'app/assets/icons/ward-icon.svg';
import { ReactComponent as AlertIcon } from 'app/assets/icons/warning-2.svg';

import { useLayout } from '../../core';
import MenuSwitchOrg from '../MenuOrg';
import { BANNER_HEIGHT, useCheckDashboardLocation } from '../header/ForecastBanner';
import { Topbar } from '../header/Topbar';
import './AsideDefault.scss';
import { useOrganizationStore } from './store';
import clsx from 'clsx';
import { KTSVG, toAbsoluteUrl } from 'theme/helpers';
import HamburgerMenu from 'theme/layout/components/HamburgerButton';

export const PointMenu = styled.div`
  width: 4px;
  height: 4px;
  background: var(--gray-700);
  border-radius: 50%;
  margin-right: 12px;
`;
export const MenuSub = styled.div<{ active?: boolean }>`
  display: flex;
  align-items: center;
  /* padding-left: 12px; */
  border-radius: 6px;
  margin-bottom: 4px;
  ${({ active }) =>
    active
      ? `  background: var(--primary-2);
    p {
      color: var(--primary);
      font-weight: 600;
    };.point {
      background: var(--primary);
    }`
      : ''}

  :hover {
    background: var(--primary-2);
    p {
      color: var(--primary);
      font-weight: 600;
    }
    .point {
      background: var(--primary);
    }

    svg path {
      stroke: var(--primary);
    }
  }
`;
export const GroupTitle = styled.div`
  height: 32px;
  display: flex;
  align-items: center;
  margin-top: 24px;
`;

const ImageLogo = styled.img`
  height: 36px;
`;

const SwitchOrganization = styled.div<{ isSystemAdmin?: boolean; isOrgAdmin?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
  height: 45px;
  border: ${({ isOrgAdmin }) => (!isOrgAdmin ? '1px solid var(--neutral-6)' : '')};
  border-radius: 8px;
  cursor: ${({ isSystemAdmin }) => (isSystemAdmin ? 'pointer' : 'default')};

  &:hover {
    border-color: ${({ isSystemAdmin }) => (isSystemAdmin ? 'var(--primary)' : '')};

    p {
      color: ${({ isSystemAdmin }) => (isSystemAdmin ? 'var(--primary)' : '')};
    }

    svg path {
      stroke: ${({ isSystemAdmin }) => (isSystemAdmin ? 'var(--primary)' : '')};
    }
  }
`;

const GroupMenuContainer = styled.div<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;

  svg path {
    stroke: ${({ active }) => (active ? 'var(--primary)' : '')};
  }
`;

type MenuItem = {
  title: React.ReactNode;
  path: string;
  icon?: string;
  className?: string;
  pathActive?: string[];
  menuIcon?: React.ElementType;
};

const MAC_AIR_SCREEN = 900;
export const ASIDE_MENU_WIDTH = 244;

export const GroupMenu = ({
  active,
  label,
  icon: Icon,
}: {
  label: React.ReactNode;
  icon: React.ElementType;
  active?: boolean;
}) => {
  return (
    <GroupMenuContainer active={active}>
      {<Icon />}
      <Typography variant="body-3">{label}</Typography>
    </GroupMenuContainer>
  );
};

const AsideDefault: FC = () => {
  const location = useLocation();
  const checkScreen = useWindowSize();
  const { config, classes } = useLayout();
  const { currentUser } = useAuth();
  const permission = usePolicyInUser(currentUser?.policy ?? []);
  const isSystemAdmin = currentUser?.role?.name === 'System Admin';
  const isOrgAdmin = currentUser?.role?.name === 'Organization Admin';

  const refreshTimestamp = useNotificationStore((s) => s.state.refreshTimestamp);
  const refreshTimestampAlert = useAlertStore((s) => s.state.refreshTimestamp);

  const unReadCount = useNotificationStore((s) => s.state.unReadCount);
  const unReadAlertCount = useAlertStore((s) => s.state.unReadCount);
  const { isDashboardPage } = useCheckDashboardLocation();

  const { offlineWard, offlineRack } = useHeartBeatStore(getOfflineCount);

  const intervalRef = useRef<any>();
  const intervalRefAlert = useRef<any>();

  const isOpenMenuOrg = useBoolean(false);
  const navigate = useNavigate();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isOpenUserMenu, setIsOpenUserMenu] = useState(false);
  const [menu, setMenu] = useState<{ [key: string]: MenuItem[] }>({});
  const isFirstMenu = useBoolean(false);

  const organization = useOrganizationStore((store) => store.state.organization);
  const organizations = useOrganizationStore((store) => store.state.organizationList);
  const forceRefresh = useOrganizationStore((store) => store.state.forceRefresh);

  function getColorFromName(name: string) {
    const colors = [
      'var(--primary)',
      'var(--success-3)',
      'var(--warning)',
      'var(--info)',
      'var(--teal)',
      'var(--pink)',
    ];

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  }

  function callAPIGetTotalUnreadAlert() {
    if (!permission.alerts || !organization?.id) return;

    getTotalUnreadAlert();
  }
  function callAPIGetTotalUnreadNoti() {
    if (!permission.notification || !organization?.id) return;

    getTotalUnreadNoti();
  }

  useEffect(() => {
    callAPIGetTotalUnreadAlert();
    callAPIGetTotalUnreadNoti();
  }, [organization?.id]);

  useEffect(() => {
    SocketClient.onAlert((data) => {
      if (data.new_alert) {
        callAPIGetTotalUnreadAlert();
      }
    });

    return () => SocketClient.offAlert();
  }, []);

  useEffect(() => {
    SocketClient.onNotification((data) => {
      if (data.new_notification) {
        callAPIGetTotalUnreadNoti();
      }
    });

    return () => SocketClient.offNotification();
  }, []);

  useEffect(() => {
    if (!permission.rack || !permission.settings || !organization?.id) return;

    getHeartBeat();
    intervalRef.current = setInterval(getHeartBeat, 60000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [permission.rack, permission.settings, organization?.id]);

  useEffect(() => {
    if (!permission.systemAdmin) return;

    const getOrgs = async () => {
      try {
        const response = await getOrganizations('page=1&limit=9999999999');

        if (response.data) {
          const res = response.data.items;
          if (isNull(organization)) {
            useOrganizationStore.getState().setOrganization(res[0].id, res[0].name, res[0].logo);
          }

          useOrganizationStore.getState().setOrganizationList(res);
        }
      } catch (error) {
        console.error(error);
      }
    };

    getOrgs();
  }, [permission.systemAdmin, forceRefresh]);

  useEffect(() => {
    if (!permission || isFirstMenu.value) {
      return;
    }
    const newMenu: {
      [key: string]: MenuItem[];
    } = {
      // SYSTEM: [],
      OVERVIEW: [],
      INVENTORY: [],
      'SYSTEM SETTINGS': [],
    };
    // if (permission.systemAdmin) {
    //   newMenu['SYSTEM'].push({
    //     title: 'Organizations',
    //     path: PATH.ORGANIZATIONS,
    //     pathActive: [PATH.ORGANIZATIONS],
    //   });
    // }

    // if (permission.systemAdmin) {
    //   newMenu['SYSTEM'].push({
    //     title: 'User roles',
    //     path: PATH.USERS_ROLE_WEB,
    //     pathActive: [PATH.USERS_ROLE_WEB, PATH.USERS_ROLE_TABLET],
    //   });
    //   newMenu['SYSTEM'].push({
    //     title: 'View permission',
    //     path: PATH.PERMISSION,
    //   });
    // }

    // if (permission.systemAdmin) {
    //   newMenu['SYSTEM'].push({
    //     title: 'User management',
    //     path: PATH.USER_MANAGEMENT_SYSTEM
    //       ? PATH.USER_MANAGEMENT_SYSTEM_WEB
    //       : PATH.USER_MANAGEMENT_SYSTEM_TABLET,
    //     pathActive: [PATH.USER_MANAGEMENT_SYSTEM_WEB, PATH.USER_MANAGEMENT_SYSTEM_TABLET],
    //   });
    // }

    // if (permission.systemAdmin) {
    //   newMenu['SYSTEM'].push({
    //     title: 'Configuration settings',
    //     path: PATH.CONFIGURATIONS,
    //     pathActive: [
    //       PATH.AUTO_LOGOUT_TIME,
    //       PATH.ITEM_UNIT,
    //       PATH.CONFIGURATION_STOCK_RUN,
    //       PATH.CONFIGURATION_WORK_SHIFT,
    //       PATH.CONFIGURATION_STOCK_VALUES,
    //     ],
    //     className: 'section-system',
    //   });
    // }

    if (permission.dashboard) {
      newMenu['OVERVIEW'].push({
        title: 'Dashboard',
        menuIcon: DashboardIcon,
        path: PATH.DASHBOARD,
      });
    }
    if (permission.report) {
      newMenu['OVERVIEW'].push({
        title: 'Report',
        menuIcon: ReportIcon,
        path: PATH.REPORT,
        pathActive: [PATH.REPORT, PATH.REPORT_DEMAND_PROJECTION_DETAIL],
      });
    }
    if (permission.notification) {
      newMenu['OVERVIEW'].push({
        title: 'Notifications',
        menuIcon: NotificationIcon,
        path: PATH.NOTIFICATIONS,
      });
    }

    if (permission.alerts) {
      newMenu['OVERVIEW'].push({
        title: 'Alerts',
        menuIcon: AlertIcon,
        path: PATH.ALERTS,
      });
    }

    if (permission.item) {
      newMenu['INVENTORY'].push({
        title: 'Items',
        menuIcon: ItemIcon,
        path: PATH.ITEMS,
      });

      newMenu['SYSTEM SETTINGS'].push({
        title: 'Devices',
        menuIcon: DeviceIcon,
        path: PATH.SYSTEM_SETTING_DEVICE_SENSOR,
        pathActive: [
          PATH.SYSTEM_SETTING_DEVICE_SENSOR,
          PATH.SYSTEM_SETTING_DEVICE_GATEWAY,
          PATH.SYSTEM_SETTING_DEVICE_TABLET,
        ],
      });
    }
    if (permission.rack) {
      newMenu['INVENTORY'].push({
        title: 'Racks',
        menuIcon: RackIcon,
        path: PATH.RACKS,
      });
    }
    if (permission.item) {
      newMenu['INVENTORY'].push({
        title: 'Stock Run',
        menuIcon: StockRunIcon,
        path: PATH.INVENTORY_STOCK_RUN,
        pathActive: [PATH.INVENTORY_STOCK_RUN, PATH.STOCK_RUN_DEMAND_PROJECTION_DETAIL],
      });
    }
    if (permission.item) {
      newMenu['INVENTORY'].push({
        title: 'Locations',
        menuIcon: WardIcon,
        path: PATH.WARDS,
        pathActive: [PATH.WARDS, PATH.ROOMS, PATH.BEDS],
      });
    }
    if (permission.userManagement || permission.userMMD || permission.userNurse) {
      newMenu['SYSTEM SETTINGS'].push({
        title: 'User accounts',
        menuIcon: UserIcon,
        path: permission.userManagement ? PATH.WEB : PATH.TABLET,
        pathActive: [PATH.WEB, PATH.TABLET],
      });
    }
    if (permission.settings) {
      newMenu['SYSTEM SETTINGS'].push({
        title: 'Configuration',
        menuIcon: CompanyInfoIcon,
        path: PATH.ORGANIZATION_SETTING,
      });
    }
    setMenu(newMenu);
    isFirstMenu.setValue(true);
  }, [permission]);

  // Close drawer when clicking outside on mobile
  useEffect(() => {
    const handleOverlayClick = () => {
      if (isDrawerOpen) handleToggleDrawer();
    };

    const overlayElement = document.querySelector('.drawer-overlay');
    if (overlayElement) overlayElement.addEventListener('click', handleOverlayClick);

    return () => {
      if (overlayElement) overlayElement.removeEventListener('click', handleOverlayClick);
    };
  }, [isDrawerOpen]);

  // Close drawer on route change for mobile
  useEffect(() => {
    if (isDrawerOpen && window.innerWidth < 993) handleToggleDrawer();
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      // If window width becomes larger than mobile breakpoint, reset drawer state
      if (window.innerWidth >= 992 && isDrawerOpen) {
        setIsDrawerOpen(false);

        // Remove drawer-on class and data attribute
        const asideElement = document.getElementById('kt_aside');
        if (asideElement) {
          asideElement.classList.remove('drawer-on');
          document.body.removeAttribute('data-kt-drawer');
        }

        // Remove overlay
        const overlayElement = document.querySelector('.drawer-overlay');
        if (overlayElement) overlayElement.classList.remove('d-block');
      }

      isOpenMenuOrg.setValue(false);
      setIsOpenUserMenu(false);
    };

    window.addEventListener('resize', handleResize);

    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [isDrawerOpen]);

  const { aside } = config;

  const isShowDangerIcon = (title: React.ReactNode) => {
    if (title === 'Locations') {
      return offlineWard > 0;
    }
    if (title === 'Racks') {
      return offlineRack > 0;
    }
    return false;
  };

  const handleToggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);

    // Toggle the drawer-on class
    const asideElement = document.getElementById('kt_aside');
    if (asideElement) {
      if (!isDrawerOpen) {
        asideElement.classList.add('drawer-on');
        document.body.setAttribute('data-kt-drawer', 'on');
        return;
      }

      asideElement.classList.remove('drawer-on');
      document.body.removeAttribute('data-kt-drawer');
    }

    // Toggle overlay
    const overlayElement = document.querySelector('.drawer-overlay');
    if (overlayElement) {
      if (!isDrawerOpen) {
        overlayElement.classList.add('d-block');
        return;
      }

      overlayElement.classList.remove('d-block');
    }
  };

  const handleSelectOrg = (id: number, name: string, logo: string) => {
    if (!organization || organization.id !== id) {
      useOrganizationStore.getState().setOrganization(id, name, logo);
      navigate(`/org/${id}${PATH.DASHBOARD}`);
    }

    isOpenMenuOrg.setValue(false);
  };

  const handleNavLinkTo = (item: MenuItem) => {
    // If the path already includes /org/, don't modify
    if (item.path.includes('/org/')) {
      return item.path;
    }

    // Get the current organization ID from the URL
    const match = location.pathname.match(/\/org\/(\d+)/);
    const urlOrgId = match ? match[1] : organization?.id;

    // If we have an organization ID in the URL, add it to the path
    if (!isPublicPath(item.path)) {
      return `/org/${urlOrgId}${item.path}`;
    }

    // Otherwise, use the original path
    return item.path;
  };

  const isPublicPath = (path: string) =>
    PUBLIC_PATHS.some((publicPath) => path.startsWith(publicPath));

  return (
    <>
      <HamburgerMenu isOpen={isDrawerOpen} toggleMenu={handleToggleDrawer} />

      {isDrawerOpen && <div className="drawer-overlay" />}

      <div
        id="kt_aside"
        className={clsx('aside', classes.aside.join(' '), {
          'd-none': !aside.display,
        })}
        data-kt-drawer="true"
        data-kt-drawer-name="aside"
        data-kt-drawer-activate="{default: true, lg: false}"
        data-kt-drawer-overlay="true"
        // data-kt-drawer-min-width="{default:'200px', '300px': '225px'}"
        data-kt-drawer-direction="start"
        data-kt-drawer-toggle="#kt_aside_toggle"
      >
        <div
          id="kt_aside_menu_wrapper"
          data-kt-scroll="true"
          data-kt-scroll-activate="{default: false, lg: true}"
          // data-kt-scroll-height="auto"
          data-kt-scroll-dependencies="#kt_header"
          data-kt-scroll-wrappers="#kt_aside"
          style={{
            paddingTop: 32,
            paddingLeft: 28,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'var(--white)',
            minWidth: ASIDE_MENU_WIDTH,
            boxSizing: 'border-box',
            // height: '100vh',
            height:
              window.innerWidth < 992
                ? '100vh'
                : `calc(100vh${isDashboardPage ? ' - ' + BANNER_HEIGHT + 'px' : ''})`,
          }}
        >
          <div style={{ paddingRight: 28 }}>
            <Link to="/">
              <ImageLogo alt="Logo" src={toAbsoluteUrl('/media/logos/logo-sims-small-v2.svg')} />
            </Link>
          </div>
          <div
            className="menu menu-column menu-active-bg menu-hover-bg menu-title-gray-700 fs-6 menu-rounded w-100"
            id="#kt_aside_menu"
            data-kt-menu="true"
            style={{ flexGrow: 1, overflow: 'auto', paddingRight: 28 }}
          >
            {Object.entries(menu).map(([groupTitle, menuItems], indexGroup) => (
              <Fragment key={groupTitle}>
                {/* Switch Organization */}
                {indexGroup === 0 && (
                  <>
                    <Tippy
                      appendTo={document.body}
                      interactive
                      arrow={false}
                      placement="right"
                      disabled={!isSystemAdmin}
                      animation="shift-away"
                      trigger="click"
                      allowHTML
                      visible={isOpenMenuOrg.value}
                      content={
                        <MenuSwitchOrg
                          organizations={organizations}
                          handleSelectOrg={handleSelectOrg}
                        />
                      }
                      onClickOutside={() => isOpenMenuOrg.setValue(false)}
                      theme={'light'}
                      className="custom-tippy"
                    >
                      <div
                        style={{ marginTop: '16px' }}
                        onClick={() => isOpenMenuOrg.setValue(!isOpenMenuOrg.value)}
                      >
                        <Typography variant="body-8" color="neutral-6" className="text-uppercase">
                          Organization
                        </Typography>
                        <SwitchOrganization isSystemAdmin={isSystemAdmin} isOrgAdmin={isOrgAdmin}>
                          {isOrgAdmin ?? (
                            <div>
                              <div
                                style={{
                                  backgroundColor: organization?.logo
                                    ? ''
                                    : getColorFromName(organization?.name || 'Unknown'),
                                  marginLeft: isOrgAdmin ? -12 : 'auto',
                                  width: 30,
                                  // overflow: 'hidden',
                                  height: 30,
                                  borderRadius: 6,
                                  flexShrink: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Typography
                                  variant="title-5"
                                  className="text-white d-flex justify-content-center align-items-center"
                                  lineHeight={30}
                                >
                                  {organization?.logo ? (
                                    <Image
                                      src={organization.logo}
                                      alt={organization?.name || 'Organization logo'}
                                      style={{
                                        height: 30,
                                        width: 30,
                                        objectFit: 'contain',
                                        borderRadius: 8,
                                        margin: auto,
                                      }}
                                    />
                                  ) : (
                                    getInitialsCharacter(organization?.name || '')
                                  )}
                                </Typography>
                              </div>
                              <Typography
                                variant="body-3"
                                color="neutral-10"
                                className="w-100"
                                style={{
                                  // maxWidth: 134,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {organization?.name}
                              </Typography>
                            </div>
                          )}
                          <div
                            className="d-flex align-items-center"
                            style={{ gap: 10, padding: 3 }}
                          >
                            <div
                              style={{
                                backgroundColor: organization?.logo
                                  ? ''
                                  : getColorFromName(organization?.name || 'Unknown'),
                                marginLeft: isOrgAdmin ? -12 : 'auto',
                                width: 30,
                                height: 30,
                                borderRadius: 6,
                                flexShrink: 0,
                              }}
                            >
                              <Typography
                                variant="title-5"
                                className="text-white d-flex justify-content-center align-items-center"
                                lineHeight={30}
                              >
                                {organization?.logo ? (
                                  <Image
                                    src={organization.logo}
                                    alt={organization?.name || 'Organization logo'}
                                    style={{
                                      maxHeight: 30,
                                      maxWidth: 30,
                                      borderRadius: 8,
                                      margin: auto,
                                      objectFit: 'contain',
                                    }}
                                  />
                                ) : (
                                  getInitialsCharacter(organization?.name || '')
                                )}
                              </Typography>
                            </div>
                            <Typography
                              variant="body-3"
                              color="neutral-10"
                              className="w-100"
                              style={{
                                maxWidth: 134,
                                display: '-webkit-box',
                                WebkitLineClamp: 2, // giới hạn 2 dòng
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                whiteSpace: 'normal', // cho phép xuống dòng
                                wordBreak: 'break-word', // ngắt từ khi quá dài
                              }}
                            >
                              {organization?.name}
                            </Typography>
                          </div>
                          {isSystemAdmin && <ArrowUpDownIcon />}
                        </SwitchOrganization>
                      </div>
                    </Tippy>
                  </>
                )}
                {groupTitle && Boolean(menuItems.length) && (
                  <GroupTitle>
                    <Typography variant="body-8" color="neutral-6" className="text-uppercase">
                      {groupTitle}
                    </Typography>
                  </GroupTitle>
                )}
                {menuItems.map((item, i) => {
                  const isPathActive =
                    item.pathActive && item.pathActive.length
                      ? Boolean(
                          item.pathActive.find((el) => {
                            // Remove org prefix from pathname for comparison
                            const pathname = location.pathname;
                            const pathWithoutOrg = pathname.replace(/^\/org\/\d+/, '');
                            return pathWithoutOrg.startsWith(el);
                          }),
                        )
                      : (() => {
                          // Remove org prefix from pathname for comparison
                          const pathname = location.pathname;
                          const pathWithoutOrg = pathname.replace(/^\/org\/\d+/, '');
                          return pathWithoutOrg.startsWith(item.path);
                        })();

                  return (
                    <MenuSub active={isPathActive} key={i}>
                      <NavLink
                        to={handleNavLinkTo(item)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          height: checkScreen.height <= MAC_AIR_SCREEN ? 30 : 38,
                          width: '100%',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {item.icon ? (
                            <span className="menu-icon" data-kt-element="icon">
                              <KTSVG path={'/media/icons' + item.icon} className="svg-icon-3" />
                            </span>
                          ) : null}
                          {/* <PointMenu className="point"></PointMenu> */}
                          <GroupMenu
                            label={item.title}
                            icon={item.menuIcon as React.ElementType}
                            active={isPathActive}
                          />
                        </div>

                        {item.title === 'Notifications' && unReadCount > 0 && (
                          <Badge value={unReadCount} />
                        )}

                        {item.title === 'Alerts' && unReadAlertCount > 0 && (
                          <Badge value={unReadAlertCount} />
                        )}

                        {/* {isShowDangerIcon(item.title) && (
                          <DangerIcon width={20} height={20} style={{ marginRight: 12 }} />
                        )} */}
                      </NavLink>
                    </MenuSub>
                  );
                })}
              </Fragment>
            ))}
          </div>
          <Topbar isOpenUserMenu={isOpenUserMenu} setIsOpenUserMenu={setIsOpenUserMenu} />
        </div>
      </div>
    </>
  );
};

export { AsideDefault };
